import { unref, type MaybeRef } from 'vue';
import { useStringFunctions } from './useStringFunctions';
import { AsKeyed } from '@apparatix/types';
import { FileExtensionToMimeType } from '@apparatix/constants/FileExtensionToMimeType';

export const useUtilities = () => {
  const { isEmptyString } = useStringFunctions();

  const forKeys = (obj: object, fn: (key: string, value: any) => void) => {
    Object.keys(obj).forEach(k => {
      if (!Object.hasOwn(obj, k)) return;
      fn(k, obj[k]);
    });
  };

  const mapValues = (obj: object, mapFn: (key: string, value: any) => any): any[] => {
    const values: any[] = [];
    forKeys(obj, (k, v) => {
      values.push(mapFn(k, v));
    });

    return values;
  };

  const randomNumber = (min = 0, max = Number.MAX_SAFE_INTEGER) => Math.random() * (max - min) + min;

  const isValidNumber = (number: string | number) => {
    if (typeof number === 'number') return true;
    return !isNaN(parseInt(number, 10));
  };

  const areAllFieldsEmpty = (obj: object) => {
    let anyFieldHasValue = false;
    forKeys(obj, (_, value) => {
      if (value) {
        anyFieldHasValue = true;
        return;
      }
      if (anyFieldHasValue) {
        return;
      }
    });

    return !anyFieldHasValue;
  };

  const isNumberEmpty = (test?: number, zeroIsEmpty: boolean = false, negativeIsEmpty: boolean = false) => {
    if (test === null || test === undefined) return true;
    if (isNaN(test)) return true;

    if (zeroIsEmpty && negativeIsEmpty) return test <= 0;
    if (zeroIsEmpty && !negativeIsEmpty) return test === 0;
    if (!zeroIsEmpty && negativeIsEmpty) return test < 0;
    return false;
  };

  /**
   *
   * @description Takes an object, and returns a new object containing only fields from the original object that have a value
   * @param input Source object
   * @param {boolean} [removeEmptyArray=true] if true, will remove array fields if they are empty
   * @param {boolean} [removeEmptyObject=true] if true, will remove objects that are defined but have no fields. Null/undefined objects are always removed
   * @param {boolean} [looseStringCheck=true] if true, will remove string fields that are not empty, but contain only whitespace. Empty strings are always removed
   * @returns A new object with the same shape as the input object, but only containing fields that had values
   */
  const getOnlyPopulatedFields = <T extends object>(input: T, removeEmptyArray: boolean = true, removeEmptyObject: boolean = true, looseStringCheck = true): T => {
    if (!input) return null;
    if (Array.isArray(input) || typeof input !== 'object') {
      console.error(`getOnlyPopulatedFields expects input type to be object, but got ${typeof input}`);
      return null;
    }

    const result: T = {} as T;
    Object.keys(input).filter(key => Object.hasOwn(input, key)).forEach(key => {
      const currentValue = input[key];
      const type = typeof currentValue;

      if (currentValue === null || currentValue === undefined) {
        return;
      }

      if (type === 'string') {
        if (!isEmptyString(currentValue, looseStringCheck)) {
          result[key] = currentValue;
        }
        return;
      }

      if (Array.isArray(currentValue)) {
        if (removeEmptyArray) {
          if (currentValue.length) {
            result[key] = currentValue;
          }
        } else {
          result[key] = currentValue;
        }
        return;
      }

      if (type === 'object') {
        if (removeEmptyObject) {
          if (Object.keys(currentValue).length) {
            result[key] = currentValue;
          }
        } else {
          result[key] = currentValue;
        }
        return;
      }

      result[key] = currentValue;
    });

    if (Object.keys(result).length === 0) {
      return null;
    }

    return result;
  };

  const isObject = (test?: unknown, allowArray: boolean = false) => {
    const baseTest = typeof test === 'object' && test !== null;

    if (!allowArray) {
      return baseTest && !Array.isArray(test);
    }

    return baseTest;
  };

  const objectContainsFields = (test: object, fields: Array<number | string | symbol> | string | number | symbol) => {
    if (!isObject(test)) return false;
    if (!Array.isArray(fields)) {
      return Object.hasOwn(test, fields);
    }

    return fields.every(field => Object.hasOwn(test, field));
  };

  type ArrayHasValuesOptions = {
    min?: number;
    max?: number;
    exactly?: number;
  }
  const arrayHasValues = (test?: MaybeRef<unknown>, options?: ArrayHasValuesOptions) => {
    if (!test) return false;

    const unwrapped = unref(test);

    if (!Array.isArray(unwrapped)) {
      return false;
    }

    const defaultOptions: ArrayHasValuesOptions = {
      min: 1,
      max: Number.POSITIVE_INFINITY,
    };

    const opts = { ...defaultOptions, ...options };

    if (opts.exactly) {
      return unwrapped.length === opts.exactly;
    }

    return unwrapped.length >= opts.min && unwrapped.length <= opts.max;
  };

  /**
   *
   * Note that this function will return FALSE if the provided test is not an object
   * This is intended to be used on something you already know is an object
   */
  const isEmptyObject = (test: object, checkArray: boolean = true) => {
    if (typeof test !== 'object') {
      return false;
    }

    if (test === null) {
      return true;
    }

    if (checkArray && Array.isArray(test)) {
      return !arrayHasValues(test);
    }

    return Object.keys(test).filter(key => Object.hasOwn(test, key)).length === 0;
  };

  const deepClone = <T>(toClone: T): T => JSON.parse(JSON.stringify(toClone));

  /**
   * Safely call a function without wrapping in `if (typeof fn === 'function')`
   */
  const safeInvoke = <TArgs extends any[], TReturn>(fn: ((...args: TArgs) => TReturn) | (() => TReturn), ...args: TArgs): TReturn => {
    if (typeof fn !== 'function') return undefined;
    if (args) {
      // eslint-disable-next-line prefer-spread
      return fn.apply(null, args);
    }

    return fn();
  };

  const isFunction = (test?: any) => typeof test === 'function';

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const isAsyncFunction = (test?: Function) => isFunction(test) && test?.constructor?.name === 'AsyncFunction';

  /**
   *
   * @description takes an array of objects, and adds a unique key to each object
   */
  const asKeyedItems = <T extends object>(items: T[]): AsKeyed<T>[] => items?.map(item => ({ ...item, key: crypto.randomUUID() }));

  /**
   * @description Picks the specified keys from one object into a new one
   * @param source Source object
   * @param keys Array of keys of source object that will be plucked into the return object
   * @returns A new object containing only the provided fields from the source object
   */
  const partial = <T extends object>(source: T, keys: Array<keyof T>): {[K in keyof T]?: T[K] } => Object.fromEntries(
    keys.map(key => {
      const value = source[key];

      if (typeof value === 'object' && value !== null) {
        return [key, deepClone(value)];
      }
      return [key, value];
    }),
  ) as {[K in keyof T]?: T[K] };

  /**
   * @description Clones an object, omitting specified keys
   * @param source Source object
   * @param keys Array of keys to omit from the return object
   * @returns new object with all of the fields of the source object, omitting the specified keys
   */
  const omit = <T extends object>(source: T, keys: Array<keyof T>, keepUndefined: boolean = true): Partial<T> => {
    if (!arrayHasValues(keys)) {
      return deepClone(source);
    }

    const keysToOmit = new Set<keyof T>([...keys]);
    const result: Array<[key: string, value: any]> = [];

    Object.keys(source).filter(key => !keysToOmit.has(key as keyof T))
      .forEach(key => {
        const value = source[key];

        if (isObject(value, true)) {
          result.push([key, deepClone(value)]);
          return;
        }

        if (value === undefined && keepUndefined) {
          result.push([key, undefined]);
          return;
        }

        result.push([key, value]);
      });

    return Object.fromEntries(result) as Partial<T>;
  };

  /**
   * @description Use to show errors/warnings that should only appear when running the site in dev mode
   * @param callPredicate predicate function to determine whether the message should be shown. Can also pass `true` to always show
   * @param message error message or instance of Error
   * @param type Optional type to determine how the message is shown. Options are console log/warn/error, or throw an error. Defaults to console.error if not provided
   */
  const developerWarning = (callPredicate: (() => boolean) | boolean, message: string | Error, type?: 'console:log' | 'console:warn' | 'console:error' | 'throw') => {
    const shouldShow = typeof callPredicate === 'function'
      ? callPredicate()
      : callPredicate;

    if (import.meta.env.DEV && shouldShow) {
      const errorMessage = message instanceof Error
        ? message.message
        : message;

      switch (type) {
        case 'console:log':
          console.log(errorMessage);
          break;
        case 'console:warn':
          console.warn(errorMessage);
          break;
        case 'console:error':
          console.error(errorMessage);
          break;
        case 'throw':
          if (message instanceof Error) {
            throw message;
          } else {
            throw new Error(message);
          }
        default:
          console.error(errorMessage);
      }
    }
  };

  /**
   *
   * @description combines arrays into a single new array. Empty arrays are discarded
   */
  const combineArrays = <T>(...args: Array<T[]>): T[] => [...args.filter(arg => arrayHasValues(arg)).flatMap(arg => arg)];

  /**
   *
   * @description sometimes you just don't feel like doing anything
   * (the actual intent of this function is for use as a default argument for Vue's inject,
   * or other similar use cases)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty-function
  const noop = (...args: any[]) => {};

  /**
   * @description Programmatically initiate a file download
   * @param fileData file data to download, or URL where file data is located
   * @param filename name of the file to download
   * @param mimeType MIME type of file
   * @param dataIsUrl if true, will fetch the URL provided in the fileData arg, and use that file contents as the file data
   */
  const downloadFile = async (fileData: BlobPart | string, filename: string, mimeType: string, dataIsUrl: boolean = false) => {
    let data: BlobPart;
    if (typeof fileData === 'string' && dataIsUrl) {
      const response = await fetch(fileData);
      const blobContent = await response?.blob();
      if (!blobContent) {
        throw new Error('Error retrieving file contents');
      }

      data = blobContent;
    } else {
      data = fileData;
    }

    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   *
   * @description Attempt to get MIME type for a file
   * @returns MIME type if able to be determined, falls back to empty string
   */
  const getMimeTypeForFilename = (filename: string) => {
    if (!filename || typeof filename !== 'string') {
      return '';
    }

    const extension = filename.split('.').pop();

    if (extension in FileExtensionToMimeType) {
      return FileExtensionToMimeType[extension];
    }

    return '';
  };

  /**
   * @description used to wait a specified number of milliseconds in an async function. This function should ONLY be used
   * for things like waiting for an animation to complete, or to run something after a short timeout. It should NOT be used
   * as an escape hatch to work around async issues or timing bugs
   * @param ms amount of milliseconds to wait
   */
  const sleep = (ms: number) => new Promise(resolve => setTimeout(() => resolve(true), ms));

  return {
    areAllFieldsEmpty,
    arrayHasValues,
    asKeyedItems,
    combineArrays,
    deepClone,
    developerWarning,
    downloadFile,
    forKeys,
    getMimeTypeForFilename,
    getOnlyPopulatedFields,
    isAsyncFunction,
    isEmptyObject,
    isFunction,
    isNumberEmpty,
    isObject,
    isValidNumber,
    mapValues,
    noop,
    objectContainsFields,
    omit,
    partial,
    randomNumber,
    safeInvoke,
    sleep,
  };
};
